/** @jsxImportSource @emotion/react */
import { ReactNode, useState } from 'react'
import { Form as FinalForm } from 'react-final-form'
import arrayMutators from 'final-form-arrays'
import createDecorator from 'final-form-focus'
import Button from '@mui/material/Button'

import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg'
import { validateRequired } from 'utils/validation'
import TextareaAutosize from 'components/TextareaAutosize/TextareaAutosize'
import { SelectField } from 'components/Select'
import { BaseItem } from 'components/Autosuggest/Autosuggest'
import ConfirmationModal from 'components/Modals/ConfirmationModal'
import * as classes from './styles'

export type FormValues = {
  id?: string
  response: string
  quartile: BaseItem
  quartileExplanation: string
  responseFeedback: string
}

type Props = {
  initialValues?: FormValues
  onSubmit: (input: FormValues) => Promise<void>
  onCancel?: () => void
  onDelete?: (id?: string) => Promise<void>
  responseReadoOnly?: boolean
  children?: ReactNode
}

export const QUARTILE_OPTIONS: BaseItem[] = [
  { id: '1', title: '1st' },
  { id: '2', title: '2nd' },
  { id: '3', title: '3rd' },
  { id: '4', title: '4th' },
]

const focusOnErrors = createDecorator<FormValues, Partial<FormValues>>()

export const Form = ({
  initialValues: initialValuesProp,
  onSubmit,
  onCancel,
  onDelete,
  responseReadoOnly,
  children,
}: Props) => {
  const isEditing = !!initialValuesProp

  const [submitting, setSubmitting] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const initialValues: FormValues = initialValuesProp || {
    response: '',
    quartile: QUARTILE_OPTIONS[0],
    quartileExplanation: '',
    responseFeedback: '',
  }

  const handleSubmit = async (values: FormValues) => {
    try {
      setSubmitting(true)
      await onSubmit(values)
    } catch (e) {
      console.error(e)
    }
    setSubmitting(false)
  }

  const handleDelete = () => {
    setIsDeleteModalOpen(true)
  }

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false)
  }

  const handleDeleteConfirm = async () => {
    try {
      setSubmitting(true)
      await onDelete?.(initialValuesProp?.id)
      setIsDeleteModalOpen(false)
    } catch (e) {
      console.error(e)
    }
    setSubmitting(false)
  }

  return (
    <>
      <FinalForm<FormValues>
        mutators={{ ...arrayMutators }}
        decorators={[focusOnErrors]}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, dirty }) => (
          <form onSubmit={handleSubmit} css={classes.form} data-testid="response-test-form">
            {children}

            <TextareaAutosize
              name="response"
              label="Response"
              css={classes.textArea}
              validate={validateRequired}
              readOnly={responseReadoOnly}
            />

            <SelectField
              name="quartile"
              label="Quartile"
              validate={validateRequired}
              items={QUARTILE_OPTIONS}
              fullWidth
            />

            <TextareaAutosize
              name="quartileExplanation"
              label="Quartile Explanation"
              css={classes.textArea}
              validate={validateRequired}
            />

            <TextareaAutosize
              name="responseFeedback"
              label="Response feedback"
              css={classes.textArea}
              validate={validateRequired}
            />

            <div css={classes.buttons}>
              {isEditing ? (
                <Button variant="outlined" color="red" size="large" disabled={submitting} onClick={handleDelete}>
                  <TrashIcon css={classes.trashIcon} />
                  Delete
                </Button>
              ) : (
                <Button variant="outlined" color="red" size="large" disabled={submitting} onClick={onCancel}>
                  Cancel
                </Button>
              )}

              {isEditing ? (
                <Button color="blue" type="submit" size="large" disabled={!dirty || submitting}>
                  Save Edits
                </Button>
              ) : (
                <Button color="blue" type="submit" size="large" disabled={!dirty || submitting}>
                  Add Content
                </Button>
              )}
            </div>
          </form>
        )}
      />
      <ConfirmationModal
        open={isDeleteModalOpen}
        title={`Deleting Training Data`}
        subtitle={`Are you sure you want to delete this Training Data?`}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        confirmText="Delete"
        closeText="Cancel"
        isLoading={submitting}
      />
    </>
  )
}
